import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/es';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import AutTicketModal from './AutTicketModal';
import Navbar from '../Navbar';

moment.locale('es');

const API_URL = process.env.REACT_APP_API_URL || 'https://botcombustible.netizar.com';

const AutGastosCombustibleList = () => {
  // Estados
  const [gastos, setGastos] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [gastoSeleccionado, setGastoSeleccionado] = useState(null);

  // Estados de paginación
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [rowCount, setRowCount] = useState(0);

  // Filtros
  const [filtros, setFiltros] = useState({
    patente: null,
    desde: null,
    hasta: null,
    estado: 'todos'
  });

  // Cargar vehículos al montar
  useEffect(() => {
    cargarVehiculos();
  }, []);

  // Cargar gastos cuando cambian los filtros o paginación
  useEffect(() => {
    cargarGastos();
  }, [paginationModel, filtros]);

  const cargarVehiculos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vehiculos/con-gastos`);
      const data = await response.json();
      setVehiculos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando vehículos:', error);
      setVehiculos([]);
    }
  };

  const cargarGastos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', paginationModel.page + 1);
      params.append('limit', paginationModel.pageSize);

      if (filtros.patente) params.append('patente', filtros.patente.patente);
      if (filtros.desde) params.append('desde', moment(filtros.desde).format('YYYY-MM-DD'));
      if (filtros.hasta) params.append('hasta', moment(filtros.hasta).format('YYYY-MM-DD'));
      if (filtros.estado !== 'todos') params.append('estado', filtros.estado);

      const response = await fetch(`${API_URL}/api/gastos?${params}`);
      const data = await response.json();

      setGastos(data.data);
      setRowCount(data.pagination.total);
    } catch (error) {
      console.error('Error cargando gastos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerTicket = (gasto) => {
    setGastoSeleccionado(gasto);
    setModalOpen(true);
  };

  const handleDescargarTicket = async (id) => {
    try {
      window.open(`${API_URL}/api/gastos/${id}/ticket`, '_blank');
    } catch (error) {
      console.error('Error descargando ticket:', error);
    }
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      patente: null,
      desde: null,
      hasta: null,
      estado: 'todos'
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setGastoSeleccionado(null);
    cargarGastos(); // Recargar lista
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'warning';
      case 'aprobado':
        return 'success';
      case 'rechazado':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'fecha_carga',
      headerName: 'Fecha',
      width: 130,
      valueFormatter: (params) => {
        return moment.parseZone(params.value).format('DD/MM/YYYY HH:mm');
      }
    },
    {
      field: 'dni_empleado',
      headerName: 'DNI',
      width: 90
    },
    {
      field: 'nombre_empleado',
      headerName: 'Empleado',
      width: 250
    },
    {
      field: 'patente_vehiculo',
      headerName: 'Patente',
      width: 100
    },
    {
      field: 'kilometraje',
      headerName: 'Kilometraje',
      width: 110,
      valueFormatter: (params) => {
        return `${params.value.toLocaleString()} km`;
      }
    },
    {
      field: 'numero_ticket',
      headerName: 'Nº Ticket',
      width: 120,
      valueFormatter: (params) => {
        return params.value || '-';
      }
    },
    {
      field: 'monto_extraido',
      headerName: 'Monto',
      width: 90,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || isNaN(params.value)) {
          return '-';
        }
        return `$${Number(params.value).toFixed(2)}`;
      }
    },
    {
      field: 'litros_extraidos',
      headerName: 'Litros',
      width: 90,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined || isNaN(params.value)) {
          return '-';
        }
        return `${Number(params.value).toFixed(2)} L`;
      }
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value.toUpperCase()}
          color={getEstadoColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleVerTicket(params.row)}
            title="Ver ticket"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleDescargarTicket(params.row.id)}
            title="Descargar"
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <>
      <Navbar />
      <Box sx={{ p: 1 }}>
        {/* Header */}
        <Typography variant="h5" sx={{ mb: 1 }}>
          Gestión de Gastos - Combustible
        </Typography>

        {/* Filtros */}
        <Card sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Filtros
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Autocomplete
                sx={{ minWidth: 250 }}
                size="small"
                options={vehiculos}
                getOptionLabel={(option) => `${option.patente} - ${option.marca} ${option.modelo}`}
                value={filtros.patente}
                onChange={(e, newValue) => setFiltros({ ...filtros, patente: newValue })}
                renderInput={(params) => <TextField {...params} label="Patente" />}
              />

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Desde"
                  value={filtros.desde}
                  onChange={(newValue) => setFiltros({ ...filtros, desde: newValue })}
                  slotProps={{
                    textField: {
                      sx: { width: 160 },
                      size: 'small'
                    }
                  }}
                />

                <DatePicker
                  label="Hasta"
                  value={filtros.hasta}
                  onChange={(newValue) => setFiltros({ ...filtros, hasta: newValue })}
                  slotProps={{
                    textField: {
                      sx: { width: 160 },
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>

              <FormControl sx={{ minWidth: 140 }} size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filtros.estado}
                  label="Estado"
                  onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="aprobado">Aprobado</MenuItem>
                  <MenuItem value="rechazado">Rechazado</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleLimpiarFiltros}
              >
                Limpiar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Tabla */}
        <Paper sx={{ height: 'calc(100vh - 220px)' }}>
          <DataGrid
            rows={gastos}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            density="compact"
            sx={{
              '& .MuiDataGrid-cell': {
                fontSize: '0.813rem',
                py: 0.5
              },
              '& .MuiDataGrid-columnHeaders': {
                minHeight: '40px !important',
                maxHeight: '40px !important',
                fontSize: '0.813rem'
              },
              '& .MuiDataGrid-row': {
                maxHeight: '40px !important',
                minHeight: '40px !important'
              }
            }}
          />
        </Paper>

        {/* Modal */}
        {gastoSeleccionado && (
          <AutTicketModal
            open={modalOpen}
            gasto={gastoSeleccionado}
            onClose={handleModalClose}
          />
        )}
      </Box>
    </>
  );
};

export default AutGastosCombustibleList;